/**
 * Panel de adjuntos del formulario.
 *
 * Es genérico como todo el renderer: no sabe de gastos, de facturas ni de
 * consorcios. Aparece cuando la metadata del modelo dice `attachments: true`
 * (`ModelSpec.attachments` en el backend) y funciona igual en los trece módulos.
 *
 * Cuatro decisiones que definen su comportamiento:
 *
 * 1. **Quien no puede escribir el registro no ve la zona de subida ni los
 *    botones de borrar.** El `can_write` lo contesta el servidor en la misma
 *    respuesta del listado, y el servidor lo vuelve a exigir en cada POST y en
 *    cada DELETE: esconder el botón es cortesía, no seguridad.
 * 2. **La descarga va por `fetch` con la sesión, no por un `<a href>`.** No hay
 *    ninguna URL del archivo que funcione sin permisos — ése es el punto entero
 *    de `nucleo/core/attachments.py` — así que el archivo se pide, llega como
 *    blob y se guarda con el nombre que manda la cabecera.
 * 3. **El tope de tamaño se comprueba antes de subir.** El servidor lo va a
 *    rechazar igual, pero mandar 40 MB por una línea de aire para que te digan
 *    que no es una falta de respeto al que está con datos móviles. El número
 *    sale del servidor (`max_bytes`), no está escrito acá.
 * 4. **Sólo sobre un registro guardado.** Un registro nuevo todavía no tiene id,
 *    y sin id no hay de qué colgar el papel.
 *
 * LA GALERÍA DE FOTOS
 * ===================
 * El comprador pidió subir *fotos* de documentación y verlas como fotos, no
 * como una lista de nombres de archivo. Los adjuntos con `mimetype` de imagen
 * se dibujan como miniaturas y se abren a pantalla completa; todo lo demás
 * sigue en la lista de siempre. Es una sola pantalla con dos formas de mostrar,
 * y no dos secciones distintas, porque una ficha real mezcla las dos cosas: la
 * foto del DNI y el PDF del poder.
 *
 * **Por qué la miniatura se baja en vez de pedirse a una URL.** Porque esa URL
 * no existe y no puede existir: `core/attachments_api.py` sirve TODO adjunto con
 * `Content-Disposition: attachment` y `X-Content-Type-Options: nosniff`, y deja
 * escrito el motivo — un archivo que el navegador muestra en el mismo origen que
 * la aplicación es un XSS almacenado esperando el tipo de archivo equivocado.
 * Así que la foto se pide con la sesión puesta, llega como blob y se dibuja con
 * `URL.createObjectURL`. La regla de seguridad no se toca; el trabajo se hace de
 * este lado.
 *
 * **Se baja UNA sola vez por foto.** El mismo object URL que dibuja la miniatura
 * es el que usa el visor a pantalla completa: agrandar una foto no vuelve a
 * pedir nada. Y se revocan todos al desmontar el panel, porque un object URL que
 * no se revoca mantiene el blob entero en memoria mientras dure la pestaña —
 * seis fotos por ficha y veinte fichas visitadas es una pestaña que crece sola.
 *
 * **Y pesan poco porque se achican al subirlas**, no al mostrarlas: ver
 * `lib/imagenes.ts`, que explica por qué se eligió reducirlas en el navegador y
 * qué se pierde con eso.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useRef, useState, type DragEvent } from 'react';

import {
  IconDownload,
  IconImage,
  IconPaperclip,
  IconTrash,
  IconX,
} from '@/components/shell/icons';
import { Button } from '@/components/shell/primitives';
import { notify } from '@/components/shell/toast';
import { ApiError, api } from '@/lib/api';
import { esFoto, prepararFotoParaSubir } from '@/lib/imagenes';
import type { Attachment, AttachmentList } from '@/lib/types';
import { ConfirmDialog } from '@/views/ConfirmDialog';
import { useCompanyId } from '@/views/queries';
import { guardarArchivo } from '@/views/ReportBar';

export interface AttachmentsPanelProps {
  /** Nombre lógico del modelo (`property.expense_line`). */
  modelName: string;
  /** Id del registro. El panel no se monta con un registro sin guardar. */
  recordId: number;
}

/** El mensaje del servidor cuando lo hay; si no, nada (el título ya dice qué pasó). */
function mensajeDe(error: unknown): string | undefined {
  return error instanceof ApiError ? error.message : undefined;
}

/** Clave de caché: como todo el renderer, arranca por la empresa activa. */
function claveAdjuntos(companyId: number | null, modelName: string, recordId: number) {
  return ['adjuntos', companyId, modelName, recordId] as const;
}

/** `1,4 MB`, `312 kB`, `0,9 kB`. Con coma decimal, que es como se escribe acá. */
export function formatearTamanio(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${bytes} B`;
}

/** `03/08/2026 14:22` a partir del ISO que manda el backend. */
function formatearFecha(iso: string | null): string {
  if (iso === null) return '';
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) return '';
  return fecha.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** La línea de abajo de cada archivo: tamaño, fecha, quién y descripción. */
function detalleDe(adjunto: Attachment): string {
  return [
    formatearTamanio(adjunto.size),
    formatearFecha(adjunto.uploaded_at),
    adjunto.uploaded_by,
    adjunto.description,
  ]
    .filter((parte) => parte !== '')
    .join(' · ');
}

/**
 * Baja las fotos de la lista y devuelve sus object URLs por id.
 *
 * En serie y no en paralelo, por el mismo motivo que las subidas: seis pedidos
 * simultáneos por la conexión de una oficina hacen que los seis tarden más.
 *
 * Las fotos que fallan quedan sin entrada en el mapa y la grilla las dibuja con
 * su recuadro de reemplazo. Eso NO es sólo defensa contra un error de red: es el
 * caso normal en el archivo suelto del comprador, donde el servidor falso
 * devuelve un PDF generado al vuelo para cualquier descarga.
 */
function useFotos(modelName: string, recordId: number, fotos: Attachment[]): Map<number, string> {
  const [urls, setUrls] = useState<Map<number, string>>(new Map());
  // Sólo los ids, y como string: sin esto el efecto se vuelve a disparar en cada
  // render porque `fotos` es un array nuevo cada vez, y la ficha se pondría a
  // bajar las mismas fotos para siempre.
  const clave = fotos.map((foto) => foto.id).join(',');

  useEffect(() => {
    let vivo = true;
    const creadas = new Map<number, string>();

    void (async () => {
      for (const foto of fotos) {
        if (!vivo) break;
        try {
          const bajado = await api.downloadAttachment(modelName, recordId, foto.id, foto.name);
          if (!vivo) break;
          // El servidor manda todo con `Content-Disposition: attachment`, así
          // que el tipo del blob es lo único que dice si esto se puede dibujar.
          if (!bajado.blob.type.startsWith('image/')) continue;
          const url = URL.createObjectURL(bajado.blob);
          creadas.set(foto.id, url);
          setUrls(new Map(creadas));
        } catch {
          // Sin miniatura. El archivo sigue estando y se sigue pudiendo bajar.
        }
      }
    })();

    return () => {
      vivo = false;
      // Sin esto, cada ficha visitada deja sus fotos enteras en memoria hasta
      // que se cierre la pestaña.
      for (const url of creadas.values()) URL.revokeObjectURL(url);
      setUrls(new Map());
    };
    // `clave` y no `fotos`: ver arriba.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelName, recordId, clave]);

  return urls;
}

export function AttachmentsPanel({ modelName, recordId }: AttachmentsPanelProps) {
  const companyId = useCompanyId();
  const queryClient = useQueryClient();
  const entrada = useRef<HTMLInputElement>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [aBorrar, setABorrar] = useState<Attachment | null>(null);
  const [bajando, setBajando] = useState<number | null>(null);
  const [ampliada, setAmpliada] = useState<Attachment | null>(null);

  const clave = claveAdjuntos(companyId, modelName, recordId);
  const consulta = useQuery({
    queryKey: clave,
    queryFn: ({ signal }) => api.listAttachments(modelName, recordId, signal),
    // Los adjuntos los sube otra persona tan seguido como uno mismo: la caché
    // caduca rápido, igual que la de los datos de negocio.
    staleTime: 15_000,
  });

  const datos: AttachmentList | undefined = consulta.data;
  const puedeEscribir = datos?.can_write === true;
  const topeBytes = datos?.max_bytes ?? 0;
  const adjuntos = datos?.results ?? [];

  const fotos = adjuntos.filter((adjunto) => esFoto(adjunto.mimetype));
  const papeles = adjuntos.filter((adjunto) => !esFoto(adjunto.mimetype));
  const urls = useFotos(modelName, recordId, fotos);

  const subir = useMutation({
    mutationFn: (archivo: File) => api.uploadAttachment(modelName, recordId, archivo),
    onSuccess: (adjunto) => {
      notify.success(`Se adjuntó «${adjunto.name}».`);
      void queryClient.invalidateQueries({ queryKey: clave });
    },
    onError: (error: unknown) => {
      notify.error('No se pudo adjuntar el archivo.', mensajeDe(error));
    },
  });

  const borrar = useMutation({
    mutationFn: (adjunto: Attachment) =>
      api.deleteAttachment(modelName, recordId, adjunto.id),
    onSuccess: () => {
      notify.success('Adjunto eliminado.');
      setABorrar(null);
      void queryClient.invalidateQueries({ queryKey: clave });
    },
    onError: (error: unknown) => {
      notify.error('No se pudo eliminar el adjunto.', mensajeDe(error));
    },
  });

  /**
   * Sube los archivos de a uno.
   *
   * En serie y no en paralelo a propósito: cinco subidas simultáneas por la
   * conexión de una oficina hacen que las cinco tarden más y que el aviso de
   * error no se sepa de cuál es.
   *
   * Las fotos pasan antes por `prepararFotoParaSubir`, que las achica en el
   * navegador. El tope se comprueba DESPUÉS de achicar y no antes, que es lo
   * que hace que una foto de 9 MB —hoy rechazada por pasarse de los 15 MB…
   * o del tope que sea— entre igual: lo que viaja es la versión reducida.
   */
  async function subirVarios(archivos: FileList | File[]): Promise<void> {
    for (const original of Array.from(archivos)) {
      const preparada = await prepararFotoParaSubir(original);
      const archivo = preparada.archivo;

      if (topeBytes > 0 && archivo.size > topeBytes) {
        notify.warning(
          `«${archivo.name}» es más grande de lo permitido.`,
          `Pesa ${formatearTamanio(archivo.size)} y el máximo por archivo es ${formatearTamanio(topeBytes)}.`,
        );
        continue;
      }
      // Se avisa que la foto se achicó. Callarlo sería que el sistema cambie el
      // archivo de alguien sin decírselo, y quien sube la foto de una escritura
      // tiene derecho a saber que lo guardado no es el original.
      if (preparada.seRedujo) {
        notify.info(
          `«${original.name}» se guardó como foto liviana.`,
          `De ${formatearTamanio(preparada.bytesOriginales)} a ${formatearTamanio(archivo.size)}, ` +
            'con el tamaño suficiente para leer un documento.',
        );
      }
      await subir.mutateAsync(archivo).catch(() => undefined);
    }
  }

  async function bajar(adjunto: Attachment): Promise<void> {
    setBajando(adjunto.id);
    try {
      const archivo = await api.downloadAttachment(modelName, recordId, adjunto.id, adjunto.name);
      guardarArchivo(archivo.blob, archivo.filename);
    } catch (error) {
      notify.error('No se pudo descargar el adjunto.', mensajeDe(error));
    } finally {
      setBajando(null);
    }
  }

  function alSoltar(evento: DragEvent<HTMLDivElement>): void {
    evento.preventDefault();
    setArrastrando(false);
    if (!puedeEscribir) return;
    if (evento.dataTransfer.files.length > 0) void subirVarios(evento.dataTransfer.files);
  }

  // El panel no se dibuja si el usuario no puede ni siquiera listar: el 403 ya
  // se contestó y no hay nada que mostrar donde iría una pared de error.
  if (consulta.isError) return null;

  return (
    <section aria-labelledby="adjuntos-titulo" className="mt-6 rounded-xl border border-line bg-surface">
      <header className="flex items-center gap-2 border-b border-line px-4 py-3 sm:px-6">
        <IconPaperclip className="h-4 w-4 text-fg-muted" />
        <h2 id="adjuntos-titulo" className="text-sm font-semibold">
          Adjuntos y documentación
        </h2>
        <span className="text-sm text-fg-muted">{adjuntos.length}</span>
        {puedeEscribir && (
          <Button
            size="sm"
            variant="secondary"
            className="ml-auto"
            loading={subir.isPending}
            onClick={() => entrada.current?.click()}
          >
            Adjuntar archivo
          </Button>
        )}
      </header>

      <input
        ref={entrada}
        type="file"
        multiple
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(evento) => {
          const elegidos = evento.target.files;
          if (elegidos !== null && elegidos.length > 0) void subirVarios(elegidos);
          // Se limpia para que elegir DOS VECES el mismo archivo vuelva a
          // disparar el `change`: sin esto, reintentar una subida fallida no
          // hace nada y parece que el botón está roto.
          evento.target.value = '';
        }}
      />

      <div
        onDragOver={(evento) => {
          evento.preventDefault();
          if (puedeEscribir) setArrastrando(true);
        }}
        onDragLeave={() => {
          setArrastrando(false);
        }}
        onDrop={alSoltar}
        className={clsx(
          'px-4 py-3 transition-colors sm:px-6',
          arrastrando && 'bg-accent/5 ring-2 ring-inset ring-accent',
        )}
      >
        {consulta.isPending ? (
          <p className="py-4 text-sm text-fg-muted">Cargando adjuntos…</p>
        ) : adjuntos.length === 0 ? (
          <p className="py-4 text-sm text-fg-muted">
            {puedeEscribir
              ? 'No hay archivos. Arrastrá el comprobante o la foto acá, o usá «Adjuntar archivo».'
              : 'No hay archivos adjuntos.'}
          </p>
        ) : (
          <>
            {fotos.length > 0 && (
              <div className="pb-1 pt-1">
                <h3 className="flex items-center gap-1.5 pb-2 text-xs font-medium text-fg-muted">
                  <IconImage className="h-3.5 w-3.5" />
                  Fotos
                  <span className="text-fg-faint">{fotos.length}</span>
                </h3>
                <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                  {fotos.map((foto) => (
                    <Miniatura
                      key={foto.id}
                      foto={foto}
                      url={urls.get(foto.id)}
                      puedeEscribir={puedeEscribir}
                      bajando={bajando === foto.id}
                      onAmpliar={() => {
                        setAmpliada(foto);
                      }}
                      onBajar={() => {
                        void bajar(foto);
                      }}
                      onBorrar={() => {
                        setABorrar(foto);
                      }}
                    />
                  ))}
                </ul>
              </div>
            )}

            {papeles.length > 0 && (
              <>
                {fotos.length > 0 && (
                  <h3 className="flex items-center gap-1.5 pb-1 pt-4 text-xs font-medium text-fg-muted">
                    <IconPaperclip className="h-3.5 w-3.5" />
                    Otros archivos
                    <span className="text-fg-faint">{papeles.length}</span>
                  </h3>
                )}
                <ul className="divide-y divide-line">
                  {papeles.map((adjunto) => (
                    <li key={adjunto.id} className="flex items-center gap-3 py-2.5">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{adjunto.name}</p>
                        <p className="truncate text-xs text-fg-muted">{detalleDe(adjunto)}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Descargar ${adjunto.name}`}
                        loading={bajando === adjunto.id}
                        onClick={() => {
                          void bajar(adjunto);
                        }}
                      >
                        <IconDownload className="h-4 w-4" />
                        <span className="hidden sm:inline">Descargar</span>
                      </Button>
                      {puedeEscribir && (
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Eliminar ${adjunto.name}`}
                          onClick={() => {
                            setABorrar(adjunto);
                          }}
                        >
                          <IconTrash className="h-4 w-4" />
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}

        {puedeEscribir && topeBytes > 0 && (
          <p className="pt-2 text-xs text-fg-muted">
            PDF, imágenes y planillas. Hasta {formatearTamanio(topeBytes)} por archivo. Las fotos
            grandes se achican solas al subirlas.
          </p>
        )}
      </div>

      <VisorDeFoto
        foto={ampliada}
        url={ampliada === null ? undefined : urls.get(ampliada.id)}
        onCerrar={() => {
          setAmpliada(null);
        }}
        onBajar={() => {
          if (ampliada !== null) void bajar(ampliada);
        }}
      />

      <ConfirmDialog
        open={aBorrar !== null}
        tone="danger"
        title="¿Eliminar el adjunto?"
        description={
          aBorrar === null
            ? ''
            : `Se borra «${aBorrar.name}» del sistema y del disco. No se puede deshacer.`
        }
        confirmLabel="Eliminar"
        loading={borrar.isPending}
        onConfirm={() => {
          if (aBorrar !== null) borrar.mutate(aBorrar);
        }}
        onCancel={() => {
          setABorrar(null);
        }}
      />
    </section>
  );
}

/**
 * Una foto de la grilla.
 *
 * Mientras `url` es `undefined` puede estar pasando una de dos cosas —todavía se
 * está bajando, o no se pudo— y el recuadro es el mismo a propósito: distinguir
 * las dos obligaría a llevar un estado por foto para una diferencia que no
 * cambia lo que la persona puede hacer (esperar o bajarla). El nombre del
 * archivo se lee igual, y el botón de descarga sigue estando.
 */
function Miniatura({
  foto,
  url,
  puedeEscribir,
  bajando,
  onAmpliar,
  onBajar,
  onBorrar,
}: {
  foto: Attachment;
  url: string | undefined;
  puedeEscribir: boolean;
  bajando: boolean;
  onAmpliar: () => void;
  onBajar: () => void;
  onBorrar: () => void;
}) {
  return (
    <li className="group relative overflow-hidden rounded-lg border border-line bg-bg">
      {url === undefined ? (
        <div className="flex aspect-square w-full flex-col items-center justify-center gap-1 p-2 text-fg-faint">
          <IconImage className="h-6 w-6" />
          <span className="line-clamp-2 text-center text-[11px] leading-tight">{foto.name}</span>
        </div>
      ) : (
        <button
          type="button"
          onClick={onAmpliar}
          className="block w-full cursor-zoom-in"
          // El texto alternativo es el nombre del archivo porque es lo único que
          // el sistema sabe de la foto. Quien no ve la pantalla necesita al
          // menos eso para saber cuál es cuál.
          aria-label={`Ampliar ${foto.name}`}
        >
          <img
            src={url}
            alt={foto.name}
            loading="lazy"
            className="aspect-square w-full object-cover transition-transform group-hover:scale-105"
          />
        </button>
      )}

      {/* La barra de acciones va SIEMPRE visible y no sólo al pasar el mouse:
          en un celular no hay «pasar el mouse», y estas fichas se miran desde
          el teléfono tanto como desde el escritorio. */}
      <div className="flex items-center gap-1 border-t border-line bg-surface px-1.5 py-1">
        <span className="min-w-0 flex-1 truncate text-[11px] text-fg-muted" title={detalleDe(foto)}>
          {foto.name}
        </span>
        <Button
          size="sm"
          variant="ghost"
          aria-label={`Descargar ${foto.name}`}
          loading={bajando}
          onClick={onBajar}
        >
          <IconDownload className="h-3.5 w-3.5" />
        </Button>
        {puedeEscribir && (
          <Button size="sm" variant="ghost" aria-label={`Eliminar ${foto.name}`} onClick={onBorrar}>
            <IconTrash className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </li>
  );
}

/**
 * La foto a pantalla completa.
 *
 * Mismo patrón de diálogo modal que `ConfirmDialog` —Escape cierra, el fondo
 * cierra, el foco vuelve a donde estaba— porque es el que el resto de la
 * aplicación ya cumple y porque una foto abierta sin salida con el teclado deja
 * atrapado a quien no usa el mouse.
 *
 * No vuelve a pedir el archivo: reusa el mismo object URL de la miniatura.
 */
function VisorDeFoto({
  foto,
  url,
  onCerrar,
  onBajar,
}: {
  foto: Attachment | null;
  url: string | undefined;
  onCerrar: () => void;
  onBajar: () => void;
}) {
  const abierto = foto !== null && url !== undefined;
  const focoPrevio = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!abierto) return;
    focoPrevio.current = document.activeElement as HTMLElement | null;

    const alTeclear = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.stopPropagation();
        onCerrar();
      }
    };
    document.addEventListener('keydown', alTeclear, true);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', alTeclear, true);
      document.body.style.overflow = overflow;
      focoPrevio.current?.focus();
    };
  }, [abierto, onCerrar]);

  if (!abierto || foto === null || url === undefined) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center p-4">
      <div className="absolute inset-0 bg-fg/70 animate-fade-in" onClick={onCerrar} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={foto.name}
        className="relative flex max-h-full w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-lg animate-slide-up"
      >
        <header className="flex items-center gap-2 border-b border-line px-4 py-2.5">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{foto.name}</p>
            <p className="truncate text-xs text-fg-muted">{detalleDe(foto)}</p>
          </div>
          <Button size="sm" variant="secondary" onClick={onBajar}>
            <IconDownload className="h-4 w-4" />
            <span className="hidden sm:inline">Descargar</span>
          </Button>
          <Button size="sm" variant="ghost" aria-label="Cerrar" autoFocus onClick={onCerrar}>
            <IconX className="h-4 w-4" />
          </Button>
        </header>
        {/* `object-contain` y no `cover`: acá se viene a LEER el papel, así que
            recortarlo para que llene el marco sería tapar justo lo que se busca. */}
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-bg p-2">
          <img src={url} alt={foto.name} className="max-h-[75vh] max-w-full object-contain" />
        </div>
      </div>
    </div>
  );
}
